import { Copy, RotateCcw, Save } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  currentAssetVersionFromError,
  inheritingRolesFor,
  libraryErrorField,
  requiredLanguagesFor,
  useAssetVersionQuery,
  useCreateAssetVersionMutation,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
  type LibraryAssetVersionDetail,
} from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import {
  attributePartsFailure,
  isPartsContent,
  isSameDraft,
  toDraft,
  toPartsContent,
  type DraftPart,
  type PartProblem,
} from "@/lib/assetContent";
import { describeSaveStatus, type SaveStatus } from "@/state/dirtyState";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardFooter, CardHeader, CardTitle } from "@/ui/Card";
import { Field, Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import {
  ErrorState,
  LoadingState,
  StaleDataNotice,
  UnavailableState,
} from "@/ui/states";
import { FieldAnchorProvider } from "@/ui/validation/FieldAnchorContext";

import { PartsEditor } from "./PartsEditor";
import { VersionContent } from "./VersionContent";

/**
 * The authoring surface for one parts-shaped library asset (#33).
 *
 * SAVE IS A PUBLISH. There is no autosave and no intermediate draft: the shared
 * library has no draft state to hold one, and inventing a client-side one would
 * create a second copy of clinical content that no project can adopt and no
 * export can resolve. Each Save appends exactly one immutable version, which is
 * why the note field sits beside the button rather than in a dialog afterwards.
 *
 * THE PIN IS TAKEN AT LOAD, NOT AT SUBMIT. `expectedHeadVersion` is the version
 * this clinician actually read and edited. Re-reading it at submit time would
 * turn the concurrency check into a formality that always passes and always
 * clobbers — the precise failure #28 built the pin to prevent. So a background
 * refetch never re-pins a dirty draft; the collision surfaces at Save, where
 * there is a clinician present to resolve it.
 */

interface EditSession {
  /** The head this draft was opened at, and what Save pins against. */
  pinned: string;
  /** The draft exactly as loaded — what Discard restores. */
  loaded: DraftPart[];
  draft: DraftPart[];
  note: string;
}

const SEEDED_REASON =
  "Seeded base-canon assets are read-only here; copy it into a new asset or override per project.";
const SEEDED_PACK_OWNED_REASON =
  "This seeded, pack-owned asset is read-only here. Its content changes only through the clinical-pack review path.";
const ARCHIVED_REASON =
  "This asset is archived. It has to be restored before a new version can be published; restoring is an administrator action.";
const NOTE_MAX_LENGTH = 500;

/**
 * `v7` -> 7. Only ever compared, never used to derive the next label — the
 * contract says so explicitly, and the server allocates under a row lock.
 */
function versionNumber(label: string): number {
  return Number.parseInt(label.slice(1), 10);
}

/**
 * Why this asset refuses edits, or `null` when it does not.
 *
 * Ordered by which refusal the service applies first, so the reason on screen is
 * the one a Save would actually hit: `create_version` checks the caller's
 * capability, then seeded, then archived. Reporting "archived" to a clinician
 * whose account cannot write at all would send them to fix the wrong thing.
 */
export function editRefusal(
  asset: LibraryAsset,
  capabilityReason: string | null,
): string | null {
  if (capabilityReason) {
    return capabilityReason;
  }
  if (asset.capabilities.seeded) {
    return SEEDED_REASON;
  }
  if (asset.capabilities.archived) {
    return ARCHIVED_REASON;
  }
  // `editable` is the server's own derivation. If it disagrees with the three
  // checks above, the server wins: a control enabled against a capability flag
  // that says no is the dead control the shell's invariants forbid.
  return asset.capabilities.editable
    ? null
    : "The Editor Service reports this asset as not editable.";
}

/**
 * A failed publish, sorted into what the clinician can do about it.
 *
 * `stale` is separated from every other 409 because its recovery is mechanical
 * and specific — see the newer version, re-pin, save — while an archived asset
 * needs a lifecycle change first. The contract splits them for the same reason:
 * `stale_asset_version` is its own code carrying its own `currentAssetVersion`.
 */
export type PublishFailure =
  | { kind: "stale"; version: string }
  | { kind: "parts"; message: string }
  | { kind: "refused"; message: string };

export function classifyPublishFailure(
  error: unknown,
  fallbackVersion: string,
): PublishFailure {
  if (!(error instanceof ApiError)) {
    return {
      kind: "refused",
      message:
        "The Editor Service could not be reached, so nothing was published. Your changes are still here.",
    };
  }
  if (error.code === "stale_asset_version") {
    // `currentAssetVersion` is what the envelope exists to carry; the asset
    // read's own head is the fallback for a service that answered the code
    // without it, which still beats telling the clinician nothing.
    return {
      kind: "stale",
      version: currentAssetVersionFromError(error) ?? fallbackVersion,
    };
  }
  if (error.status === 422 && libraryErrorField(error)?.startsWith("content")) {
    return { kind: "parts", message: error.message };
  }
  return { kind: "refused", message: error.message };
}

export function SaveStatusLine({
  status,
  lastSavedAt,
}: {
  status: SaveStatus;
  lastSavedAt: string | null;
}) {
  return (
    <p
      role="status"
      aria-live="polite"
      className={
        status === "error"
          ? "text-sm text-destructive"
          : "text-sm text-muted-foreground"
      }
    >
      {describeSaveStatus(status, lastSavedAt)}
    </p>
  );
}

/**
 * The recovery for a save refused as stale.
 *
 * The clinician's work is never touched here. The two ways out both start by
 * SHOWING them what landed while they were typing, and neither is available
 * until that content has actually loaded — the same fail-closed rule
 * `LifecycleDialog` applies to adoption impact, and for the same reason: a
 * decision made without seeing the thing it is about is not a decision.
 *
 * Shared with #35's alert-matrix editor rather than reimplemented there: the
 * recovery is a conversation with the SERVICE about versions, identical for
 * every content kind, and `VersionContent` already renders whichever kind came
 * back. A second copy would be a second place for "publishing on top of it
 * keeps both versions" to drift.
 */
export function ConflictRecovery({
  assetId,
  version,
  onRebase,
  onDiscardMine,
  rebaseRefusal = null,
}: {
  assetId: string;
  version: string;
  onRebase: () => void;
  /** Why keeping the stale draft would be unsafe for this content kind. */
  rebaseRefusal?: string | null;
  /**
   * Takes the loaded detail rather than a bare signal.
   *
   * The head read that feeds the editor is keyed on the version the ASSET read
   * reports, and after a refused publish that read may not have caught up yet.
   * Handing over the version actually on screen means the clinician gets the
   * content they just chose, not whatever the other query happens to hold.
   */
  onDiscardMine: (detail: LibraryAssetVersionDetail) => void;
}) {
  const newer = useAssetVersionQuery(assetId, version);

  return (
    <div
      role="alert"
      className="space-y-3 rounded-md border border-warning bg-warning/10 p-4"
    >
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          This asset moved to {version} while you were editing.
        </p>
        <p className="text-sm text-muted-foreground">
          Nothing was published and nothing you typed was lost. {version} is
          shown below. Reading it is the point of this step: publishing on top
          of it makes your content the new head, and {version} stays in the
          version history either way.
        </p>
      </div>

      <div className="rounded-md border border-border bg-card p-3">
        {newer.isPending ? (
          <LoadingState label={`Loading content at ${version}`} rows={2} />
        ) : newer.isError ? (
          <ErrorState
            title={`Content at ${version} could not be loaded`}
            description="Until it loads, neither recovery is offered — choosing between two versions without seeing one of them is not a choice."
            onRetry={() => void newer.refetch()}
          />
        ) : (
          <VersionContent content={newer.data.content} />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!newer.isSuccess || rebaseRefusal !== null}
          onClick={onRebase}
        >
          Keep my changes and publish on top of {version}
        </Button>
        <Button
          variant="outline"
          disabled={!newer.isSuccess}
          onClick={() => {
            if (newer.isSuccess) {
              onDiscardMine(newer.data);
            }
          }}
        >
          Discard my changes and edit {version}
        </Button>
      </div>
      {rebaseRefusal !== null ? (
        <p role="status" className="text-sm text-destructive">
          {rebaseRefusal}
        </p>
      ) : null}
    </div>
  );
}

export function AssetEditor({
  asset,
  onCopy,
}: {
  asset: LibraryAsset;
  /** Opens the create dialog seeded from this asset. */
  onCopy?: () => void;
}) {
  const write = useCapability("library.write");
  const head = useAssetVersionQuery(asset.id, asset.latestVersion);
  const publish = useCreateAssetVersionMutation(asset.id);
  // The service's own policy read, on the part-manifest's precedent and under
  // its rule: it describes the SERVICE rather than the asset, so it is a
  // separate query, it never re-seeds a draft or moves the pin, and an editor
  // with content in hand never waits for it. Late or absent, the language
  // control simply does not appear and the server's refusal still decides.
  const registry = useLibraryPlacementRegistryQuery();

  const [session, setSession] = useState<EditSession | null>(null);
  const [clientProblems, setClientProblems] = useState<PartProblem[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const detail: LibraryAssetVersionDetail | undefined = head.data;
  const dirty = session !== null && !isSameDraft(session.draft, session.loaded);

  // Seeding during render rather than in an effect: the draft is derived from
  // the loaded version, and an effect would render one frame of an empty editor
  // over content that is already in hand.
  //
  // Two guards, and both are load-bearing.
  //
  // A dirty draft is never re-seeded, so a background refetch cannot overwrite
  // unsaved work — the collision it would have hidden is raised at Save
  // instead, by the pin.
  //
  // The pin only ever moves FORWARD. A successful publish sets it to the
  // version it just created, while the asset read that names the head is still
  // refetching; for that window the head query is keyed on the OLD head and
  // holds the old content. Without this, the editor would seed backwards and
  // show the version it had just replaced — and a clinician typing during that
  // flash would be typing into a version that is no longer the head.
  if (detail && isPartsContent(detail.content)) {
    const newer =
      session !== null &&
      versionNumber(detail.version) > versionNumber(session.pinned);
    if (session === null || (newer && !dirty)) {
      const parts = toDraft(detail.content);
      setSession({
        pinned: detail.version,
        loaded: parts,
        draft: parts,
        note: "",
      });
      setClientProblems([]);
    }
  }

  // The blocking states apply only BEFORE there is a draft. Once a clinician
  // has one, a background head read that is pending or failing must not replace
  // the editor with a skeleton or an error panel: the work is unsaved, and
  // taking it off screen to report a refetch is a worse failure than the
  // refetch. The head query re-keys on every publish and on every stale
  // refusal, so this window is reached routinely rather than exceptionally.
  if (session === null) {
    if (head.isPending) {
      return <LoadingState label="Loading asset content" rows={4} />;
    }
    if (head.isError) {
      return (
        <ErrorState
          title="This asset's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          onRetry={() => void head.refetch()}
        />
      );
    }
  }

  if (head.data && !isPartsContent(head.data.content)) {
    // Questionnaires (#34) and alert matrices (#35) are typed content with
    // their own invariants — score ranges, routing coverage, destination
    // references — and an editor that treated them as ordered documents would
    // be the wrong editor rather than an incomplete one.
    return (
      <div className="space-y-4">
        <UnavailableState
          title="This asset type has its own editor"
          description="Its content is typed rather than a list of documents, so it is shown read-only here."
          blockedBy={
            head.data.assetType === "questionnaire"
              ? "Delivered by issue #34."
              : "Delivered by issue #35."
          }
        />
        <Card>
          <CardHeader>
            <CardTitle>Content at {head.data.version} (read-only)</CardTitle>
          </CardHeader>
          <CardBody>
            <VersionContent content={head.data.content} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (session === null) {
    // Reached when the head loaded but its content is not parts-shaped and the
    // branch above already returned, or on the frame before the seeding block
    // takes effect. Honest about the ordering rather than asserting past it.
    return <LoadingState label="Preparing the editor" rows={4} />;
  }

  const baseRefusal = editRefusal(asset, write.reason);
  const refusal =
    baseRefusal === SEEDED_REASON && !onCopy
      ? SEEDED_PACK_OWNED_REASON
      : baseRefusal;
  const readOnly = refusal !== null;
  const failure = publish.isError
    ? classifyPublishFailure(publish.error, asset.latestVersion)
    : null;

  const serverProblems: PartProblem[] =
    failure?.kind === "parts"
      ? (() => {
          const indexes = attributePartsFailure(failure.message, session.draft);
          return indexes.length > 0
            ? indexes.map((index) => ({ index, message: failure.message }))
            : [{ index: null, message: failure.message }];
        })()
      : [];

  const status: SaveStatus = publish.isPending
    ? "saving"
    : failure !== null
      ? "error"
      : dirty
        ? "dirty"
        : lastSavedAt
          ? "saved"
          : "clean";

  function updateDraft(parts: DraftPart[]) {
    setSession((current) => (current ? { ...current, draft: parts } : current));
    // Last attempt's refusals describe content that no longer exists. Leaving
    // them on screen would attribute a stale message to whatever now occupies
    // that position.
    setClientProblems([]);
    publish.reset();
  }

  function discard() {
    setSession((current) =>
      current ? { ...current, draft: current.loaded, note: "" } : current,
    );
    setClientProblems([]);
    publish.reset();
  }

  function save() {
    const current = session;
    if (!current) {
      return;
    }
    const result = toPartsContent(current.draft);
    if (!result.ok) {
      setClientProblems(result.problems);
      return;
    }
    setClientProblems([]);
    publish.mutate(
      {
        expectedHeadVersion: current.pinned,
        content: result.content,
        note: current.note.trim() === "" ? null : current.note.trim(),
      },
      {
        onSuccess(published) {
          // The published version IS the new baseline: re-deriving the draft
          // from it (rather than keeping the buffers) means Discard restores
          // what was actually stored, and a structured part that the server
          // canonicalised differently shows its stored form immediately.
          const parts = isPartsContent(published.content)
            ? toDraft(published.content)
            : current.draft;
          setSession({
            pinned: published.version,
            loaded: parts,
            draft: parts,
            note: "",
          });
          setLastSavedAt(published.createdAt);
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>
          {readOnly
            ? `Content at ${session.pinned} (read-only)`
            : `Editing ${session.pinned} · publishing creates the next version`}
        </CardTitle>
        {asset.capabilities.seeded && onCopy ? (
          <Button variant="outline" size="sm" onClick={onCopy}>
            <Copy aria-hidden="true" className="h-4 w-4" />
            Copy into a new asset
          </Button>
        ) : null}
      </CardHeader>

      <CardBody className="space-y-4">
        {/* Kept mounted and toggled, so the message is announced into the
            existing live region rather than arriving with a new one. */}
        <StaleDataNotice
          updatedAt={head.dataUpdatedAt}
          onRetry={() => void head.refetch()}
          visible={head.isError && !readOnly}
        />

        {failure?.kind === "stale" ? (
          <ConflictRecovery
            assetId={asset.id}
            version={failure.version}
            onRebase={() => {
              setSession((current) =>
                current ? { ...current, pinned: failure.version } : current,
              );
              publish.reset();
            }}
            onDiscardMine={(newer) => {
              const parts = isPartsContent(newer.content)
                ? toDraft(newer.content)
                : [];
              setSession({
                pinned: newer.version,
                loaded: parts,
                draft: parts,
                note: "",
              });
              setClientProblems([]);
              publish.reset();
            }}
          />
        ) : null}

        {failure?.kind === "refused" ? (
          <Notice role="alert" tone="destructive">
            {failure.message}
          </Notice>
        ) : null}

        {/* One provider per asset surface: findings resolve against the parts
            actually on screen, and unmounting the editor takes its anchors with
            it so a stale path can never resolve to a control that has gone. */}
        <FieldAnchorProvider>
          <PartsEditor
            parts={session.draft}
            onChange={updateDraft}
            readOnly={readOnly}
            readOnlyReason={refusal}
            problems={[...clientProblems, ...serverProblems]}
            requiredLanguages={requiredLanguagesFor(
              registry.data,
              asset.assetType,
            )}
            inheritingRoles={inheritingRolesFor(
              registry.data,
              asset.assetType,
            )}
          />
        </FieldAnchorProvider>
      </CardBody>

      {readOnly ? null : (
        <CardFooter className="flex-wrap justify-between gap-3">
          <div className="min-w-[16rem] flex-1">
            <Field
              label="Version note"
              description="Recorded against the version this Save creates, and shown in the history beside it."
            >
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  value={session.note}
                  maxLength={NOTE_MAX_LENGTH}
                  aria-describedby={describedBy}
                  placeholder="What changed, and why"
                  onChange={(event) =>
                    setSession((current) =>
                      current
                        ? { ...current, note: event.target.value }
                        : current,
                    )
                  }
                />
              )}
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <SaveStatusLine status={status} lastSavedAt={lastSavedAt} />
            <Button
              variant="outline"
              disabled={!dirty || publish.isPending}
              onClick={discard}
            >
              <RotateCcw aria-hidden="true" className="h-4 w-4" />
              Discard changes
            </Button>
            <Button disabled={!dirty || publish.isPending} onClick={save}>
              <Save aria-hidden="true" className="h-4 w-4" />
              Publish next version
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
}
